import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SEOHead from './SEOHead';

const SECURITY_QUESTIONS = [
  "What was the name of your first pet?",
  "What city were you born in?",
  "What is your mother's maiden name?",
  "What was the name of your elementary school?",
  "What is your favorite movie?",
  "What was your childhood nickname?",
  "What street did you grow up on?",
  "What is the name of your favorite childhood friend?"
];

const TicketAuth = () => {
  const [mode, setMode] = useState('login'); // login, register, forgot, reset, 2fa
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [securityQuestion, setSecurityQuestion] = useState('');
  const [securityAnswer, setSecurityAnswer] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, verify2FA, cancel2FA, pending2FA } = useAuth();
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const result = await login(email, password);
      if (result.requires_2fa) {
        setMode('2fa');
        setLoading(false);
        return;
      }
      if (result.is_admin) {
        navigate('/support/admin');
      } else {
        navigate('/support/dashboard');
      }
    } catch (err) {
      setError('Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const user = await verify2FA(totpCode);
      if (user.is_admin) {
        navigate('/support/admin');
      } else {
        navigate('/support/dashboard');
      }
    } catch (err) {
      setError(err.message || 'Invalid 2FA code');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel2FA = () => {
    cancel2FA();
    setMode('login');
    setTotpCode('');
    setError('');
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (!securityQuestion) {
      setError('Please select a security question');
      return;
    }
    
    if (!securityAnswer.trim()) {
      setError('Please provide an answer to the security question');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          first_name: firstName,
          last_name: lastName,
          company,
          phone,
          security_question: securityQuestion,
          security_answer: securityAnswer
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // Check for duplicate email error
        if (data.detail && data.detail.includes('already registered')) {
          throw new Error('An account with this email already exists. Please sign in instead.');
        }
        throw new Error(data.detail || 'Registration failed');
      }
      
      // Auto login after successful registration
      const result = await login(email, password);
      if (result.requires_2fa) {
        setMode('2fa');
      } else if (result.is_admin) {
        navigate('/support/admin');
      } else {
        navigate('/support/dashboard');
      }
    } catch (err) {
      // Handle specific error messages
      if (err.message.includes('already exists') || err.message.includes('already registered')) {
        setError('An account with this email already exists. Please sign in instead.');
      } else if (err.message.includes('body stream') || err.message.includes('JSON')) {
        setError('An account with this email already exists. Please sign in instead.');
      } else {
        setError(err.message || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const data = await response.json();
      setSuccess(data.message || 'If an account exists with this email, you will receive a password reset link.');
      setEmail('');
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderLogin = () => (
    <form onSubmit={handleLogin} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full px-4 py-2 bg-slate-800 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Password</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className="w-full px-4 py-2 bg-slate-800 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
      
      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 bg-primary hover:bg-primary/90 text-black font-semibold rounded-lg transition-colors disabled:opacity-50"
      >
        {loading ? 'Please wait...' : 'Sign In'}
      </button>
      
      <div className="text-center">
        <button
          type="button"
          onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }}
          className="text-primary hover:underline text-sm"
        >
          Forgot your password?
        </button>
      </div>
    </form>
  );

  const renderRegister = () => (
    <form onSubmit={handleRegister} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">First Name *</label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            className="w-full px-4 py-2 bg-slate-800 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Last Name *</label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            className="w-full px-4 py-2 bg-slate-800 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Company</label>
        <input
          type="text"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          className="w-full px-4 py-2 bg-slate-800 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Phone</label>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="w-full px-4 py-2 bg-slate-800 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Email *</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full px-4 py-2 bg-slate-800 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Password *</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            className="w-full px-4 py-2 bg-slate-800 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Confirm Password *</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={6}
            className="w-full px-4 py-2 bg-slate-800 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Security Question *</label>
        <select
          value={securityQuestion}
          onChange={(e) => setSecurityQuestion(e.target.value)}
          required
          className="w-full px-4 py-2 bg-slate-800 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="" className="bg-slate-800 text-white">Select a security question</option>
          {SECURITY_QUESTIONS.map((q, i) => (
            <option key={i} value={q} className="bg-slate-800 text-white">{q}</option>
          ))}
        </select>
        <p className="text-xs text-gray-500 mt-1">This will be used to verify your identity if you forget your password.</p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Security Answer *</label>
        <input
          type="text"
          value={securityAnswer}
          onChange={(e) => setSecurityAnswer(e.target.value)}
          required
          placeholder="Your answer"
          className="w-full px-4 py-2 bg-slate-800 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
      
      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 bg-primary hover:bg-primary/90 text-black font-semibold rounded-lg transition-colors disabled:opacity-50"
      >
        {loading ? 'Please wait...' : 'Create Account'}
      </button>
    </form>
  );

  const renderForgotPassword = () => (
    <form onSubmit={handleForgotPassword} className="space-y-4">
      <p className="text-gray-400 text-sm mb-4">
        Enter your email address and we'll send you a link to reset your password.
      </p>
      
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="w-full px-4 py-2 bg-slate-800 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
      
      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 bg-primary hover:bg-primary/90 text-black font-semibold rounded-lg transition-colors disabled:opacity-50"
      >
        {loading ? 'Sending...' : 'Send Reset Link'}
      </button>
      
      <div className="text-center">
        <button
          type="button"
          onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
          className="text-gray-400 hover:text-white text-sm"
        >
          ← Back to Sign In
        </button>
      </div>
    </form>
  );

  const render2FA = () => (
    <form onSubmit={handle2FAVerify} className="space-y-4">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
          </svg>
        </div>
        <p className="text-gray-400 text-sm">
          Enter the 6-digit code from your authenticator app
        </p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">Authentication Code</label>
        <input
          type="text"
          value={totpCode}
          onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          required
          maxLength={6}
          placeholder="000000"
          className="w-full px-4 py-3 bg-slate-800 border border-white/20 rounded-lg text-white text-center text-2xl tracking-widest placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-primary font-mono"
          autoComplete="one-time-code"
          data-testid="2fa-code-input"
        />
      </div>
      
      <button
        type="submit"
        disabled={loading || totpCode.length !== 6}
        className="w-full py-3 bg-primary hover:bg-primary/90 text-black font-semibold rounded-lg transition-colors disabled:opacity-50"
        data-testid="2fa-verify-btn"
      >
        {loading ? 'Verifying...' : 'Verify'}
      </button>
      
      <div className="text-center">
        <button
          type="button"
          onClick={handleCancel2FA}
          className="text-gray-400 hover:text-white text-sm"
        >
          ← Back to Sign In
        </button>
      </div>
    </form>
  );

  return (
    <>
      <SEOHead
        title={`${mode === 'login' ? 'Sign In' : mode === 'register' ? 'Register' : mode === '2fa' ? 'Two-Factor Authentication' : 'Forgot Password'} - King of IT Support Portal`}
        description="Access your King of IT support portal. Submit tickets, track requests, and get help from Toronto's trusted IT support team. Call 647-951-3653."
        path="/support"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">
              King of IT Support
            </h1>
            <p className="text-gray-400">
              {mode === 'login' && 'Sign in to your account'}
              {mode === 'register' && 'Create a new account'}
              {mode === 'forgot' && 'Reset your password'}
              {mode === '2fa' && 'Two-Factor Authentication'}
            </p>
          </div>
          
          <div className="bg-white/5 backdrop-blur-lg rounded-xl p-8 border border-white/10">
            {error && (
              <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-6">
                {error}
              </div>
            )}
            
            {success && (
              <div className="bg-green-500/20 border border-green-500/50 text-green-400 px-4 py-3 rounded-lg mb-6">
                {success}
              </div>
            )}
            
            {mode === 'login' && renderLogin()}
            {mode === 'register' && renderRegister()}
            {mode === 'forgot' && renderForgotPassword()}
            {mode === '2fa' && render2FA()}
            
            {mode !== 'forgot' && mode !== '2fa' && (
              <div className="mt-6 text-center">
                <button
                  onClick={() => {
                    setMode(mode === 'login' ? 'register' : 'login');
                    setError('');
                    setSuccess('');
                  }}
                  className="text-primary hover:underline"
                >
                  {mode === 'login' ? "Don't have an account? Register" : 'Already have an account? Sign In'}
                </button>
              </div>
            )}
          </div>
          
          <div className="mt-6 text-center">
            <a href="/" className="text-gray-400 hover:text-white transition-colors">
              ← Back to Website
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default TicketAuth;
